import { ControllerHand } from "flavr-common";
import { CylinderGeometry, Mesh, MeshBasicMaterial, Raycaster, Vector3 } from "three";
import { ControllerConnectedMessage, ControllerDisconnectedMessage, ControllerMovedMessage } from "../../messages/controller/ControllerMessages";
import { Quaternion } from "flavr-math";
import { renderer } from "../rendering/RendererGlobals";
import { mainScene } from "../rendering/mainScene";
/**
 * Responsible for forwarding the controller selection ray to the gizmo to allow for a similar experience in
 * desktop and vr mode. It handles the interaction of both controllers with the gizmo.
 */
export class GizmoVRControllerSupport {
    constructor(controls, connection) {
        /// User overridable events ///
        this.onSelectEnd = () => { };
        this.controls = controls;
        this.connection = connection;
        this.controllers = new Map();
        this.activeController = undefined;
        this.hoveredControllers = [];
        this.tempRayCaster = new Raycaster();
        this.connection.subscribeByType(ControllerConnectedMessage, (msg) => {
            this.handleControllerConnected(msg);
        });
        this.connection.subscribeByType(ControllerDisconnectedMessage, (msg) => {
            this.handleControllerDisconnected(msg);
        });
        this.connection.subscribeByType(ControllerMovedMessage, (msg) => {
            this.handleControllerMove(msg);
        });
        // Setup controller event handlers
        const controller1 = renderer.xr.getController(0);
        controller1.addEventListener("selectstart", (event) => {
            this.handleControllerSelect(event);
        });
        controller1.addEventListener("selectend", (event) => {
            this.handleControllerSelect(event);
        });
        mainScene.add(controller1);
        const controller2 = renderer.xr.getController(1);
        controller2.addEventListener("selectstart", (event) => {
            this.handleControllerSelect(event);
        });
        controller2.addEventListener("selectend", (event) => {
            this.handleControllerSelect(event);
        });
        mainScene.add(controller2);
        // Debug
        const debugRayGeometry = new CylinderGeometry(0.01, 0.01, 8);
        debugRayGeometry.translate(0, 4, 0);
        this.debugRayMesh = new Mesh(debugRayGeometry, new MeshBasicMaterial({ color: 0xff0000 }));
        this.debugRayMesh.visible = false;
        mainScene.add(this.debugRayMesh);
    }
    toggleDebugRayVisibility() {
        this.debugRayMesh.visible = !this.debugRayMesh.visible;
    }
    resetState() {
        this.hoveredControllers = [];
        this.activeController = undefined;
    }
    /// Event handlers ///
    handleControllerConnected(msg) {
        this.controllers.set(msg.uid, {
            uid: msg.uid,
            pos: new Vector3(...msg.rayOrigin),
            dir: new Vector3(...msg.rayDirection),
            worldPos: new Vector3(),
            worldDir: new Vector3(),
            hand: msg.hand === ControllerHand.RightHand ? "right" : "left"
        });
    }
    handleControllerDisconnected(msg) {
        const controller = this.controllers.get(msg.uid);
        if (controller === undefined)
            return;
        // Remove controller from active and hovered controller
        const index = this.hoveredControllers.indexOf(controller, 0);
        if (index > -1) {
            this.hoveredControllers.splice(index, 1);
        }
        if (this.activeController === controller)
            this.activeController = undefined;
        // Remove from list of connected controllers
        this.controllers.delete(msg.uid);
    }
    handleControllerMove(msg) {
        const controller = this.controllers.get(msg.uid);
        if (controller === undefined)
            return;
        // Compute ray pose
        const orientation = new Quaternion().fromArray(msg.orientation);
        const direction = controller.dir.clone().applyQuaternion(orientation);
        let origin = new Vector3().fromArray(msg.position);
        origin = controller.pos.clone().applyQuaternion(orientation).multiplyScalar(msg.scale).add(origin);
        controller.worldPos.copy(origin);
        controller.worldDir.copy(direction);
        this.checkIfControllerIntersectsGizmo(controller);
        this.handleActiveControllerMove(controller);
        // Visualize debug ray if enabled
        if (this.debugRayMesh.visible) {
            this.debugRayMesh.position.copy(origin);
            const ori = new Quaternion().setFromUnitVectors(new Vector3(0, 1, 0), direction);
            this.debugRayMesh.rotation.setFromQuaternion(ori);
        }
    }
    handleControllerSelect(event) {
        const handedness = event.data.handedness;
        if (handedness === undefined)
            return;
        switch (event.type) {
            case "selectstart": {
                if (this.activeController !== undefined)
                    return;
                // Controller must hover over the gizmo first before it can become active
                const controller = this.findControllerByHandedness(handedness);
                if (controller === undefined || !this.hoveredControllers.includes(controller))
                    return;
                this.controls.getRaycaster().set(controller.worldPos, controller.worldDir);
                this.controls.pointerHover(null);
                this.controls.pointerDown(null);
                this.activeController = controller;
                break;
            }
            case "selectend":
                if (!this.isControllerActiveController(handedness))
                    return;
                this.controls.pointerUp(null);
                this.onSelectEnd();
                this.activeController = undefined;
                break;
        }
    }
    /// Helper methods ///
    checkIfControllerIntersectsGizmo(controller) {
        // We are only interested in intersections of type mesh as these are the visible objects
        this.tempRayCaster.set(controller.worldPos, controller.worldDir);
        let intersections = this.tempRayCaster.intersectObject(this.controls.getHelper(), true);
        intersections = intersections.filter((intersection) => intersection.object.visible && intersection.object.constructor === Mesh);
        if (intersections.length === 0) {
            const index = this.hoveredControllers.indexOf(controller, 0);
            if (index > -1) {
                this.hoveredControllers.splice(index, 1);
            }
            else
                return;
        }
        else {
            if (!this.hoveredControllers.includes(controller)) {
                this.hoveredControllers.push(controller);
            }
        }
        this.controls.getRaycaster().set(controller.worldPos, controller.worldDir);
        this.controls.pointerHover(null);
    }
    handleActiveControllerMove(controller) {
        if (controller === this.activeController) {
            this.controls.getRaycaster().set(controller.worldPos, controller.worldDir);
            this.controls.pointerMove(null);
        }
    }
    isControllerActiveController(handedness) {
        return this.activeController === this.findControllerByHandedness(handedness);
    }
    findControllerByHandedness(handedness) {
        let foundController;
        this.controllers.forEach((controller) => {
            if (controller.hand === handedness)
                foundController = controller;
        });
        return foundController;
    }
}
